import Vue from "vue";
import { v4 } from "uuid";
import { Purchase } from "@/models/base";
import { InboxreaderService, InboxReaderType } from "@/services/inboxreader-service";
import { InboxTypeState } from "./inbox-type-state";
import { InboxTypeStateInbox } from "./inbox-type-state-inbox";
import { InboxTypeStatePeppol } from "./inbox-type-state-peppol";
import { InboxreaderPurchase } from "../purchase/inboxreader-purchase";
import { InboxreaderPurchaseFile } from "../purchase/inboxreader-purchase-file";

export type InboxSource = "peppol" | "other";

export const INBOX_SOURCES:InboxSource[] = ["peppol", "other"];

export function purchaseSource(purchase:InboxreaderPurchase):InboxSource{
	return purchase.PeppolOrder ? "peppol" : "other";
}

// visibleFiles returns the files of a purchase that get a card of their own: a
// peppol document is one card, a mail or upload one card per attachment.
export function visibleFiles(purchase:InboxreaderPurchase, archive:boolean):InboxreaderPurchaseFile[]{
	if (purchase.PeppolOrder) return purchase.Files.slice(0, 1);
	return purchase.Files.filter(f=>f.Processed == archive);
}

// selectionEntry is what gets selected when a card is picked: the whole peppol
// document, or the purchase narrowed down to the one attachment of the card.
export function selectionEntry(purchase:InboxreaderPurchase, file:InboxreaderPurchaseFile):InboxreaderPurchase{
	if (purchase.PeppolOrder) return purchase;
	let entry = new InboxreaderPurchase(purchase.getJSON());
	entry.Files = [file];
	return entry;
}

// InboxListState is the one list of the inbox. It holds peppol documents and
// mail/upload documents together; the tabs only narrow down which sources are
// shown. Creating a purchase is handed to the state that knows that kind of
// document.
export class InboxListState extends InboxTypeState {
	public sources:InboxSource[] = [...INBOX_SOURCES];
	// counts holds how many cards each source has for the current filters, so
	// every tab can show its own number whichever tab is open.
	public counts:Record<InboxSource, number> = {peppol: 0, other: 0};
	// countsLoaded stays false while the server does not know the counts yet.
	public countsLoaded:boolean = false;
	private peppolState:InboxTypeStatePeppol;
	private inboxState:InboxTypeStateInbox;
	private countsThread:string = "";

	public constructor(vue:Vue){
		super(vue);
		this.peppolState = new InboxTypeStatePeppol(vue);
		this.inboxState = new InboxTypeStateInbox(vue);
	}

	// visibleCount is the number of documents in the list: a peppol document
	// counts as one card, a mail or upload as one card per attachment.
	public get visibleCount():number{
		// Fall back on the number of records of the list itself as long as the
		// counts are unknown.
		if (!this.countsLoaded) return this.unprocessedCount;
		return this.countOf(this.sources);
	}

	public countOf(sources:InboxSource[]):number{
		return sources.reduce((total, source)=>total + this.counts[source], 0);
	}

	async fetch(processed:boolean, search:string = "", labelIds:number[] = []){
		await super.fetch(processed, search, labelIds);
		await this.loadCounts(processed, search, labelIds);
	}

	private async loadCounts(processed:boolean, search:string, labelIds:number[]){
		let threadId = v4();
		this.countsThread = threadId;
		try{
			let counts = await InboxreaderService.fetchDocumentCounts(this.getFetchOptions(processed, search, labelIds));
			if (this.countsThread != threadId) return;
			this.counts = counts;
			this.countsLoaded = true;
		}catch(err){
			this.countsLoaded = false;
			console.error(err);
		}
	}

	// A card is one peppol document or one attachment, so a range runs over
	// the cards and not over the purchases.
	protected orderedEntries():InboxreaderPurchase[]{
		let entries:InboxreaderPurchase[] = [];
		for (let purchase of this.purchases) {
			for (let file of visibleFiles(purchase, this.archive)) {
				entries.push(selectionEntry(purchase, file));
			}
		}
		return entries;
	}

	protected getInboxTypes():InboxReaderType[]{
		if (this.sources.length != 1) return [];
		return this.sources[0] == "peppol" ? ["peppol"] : ["inbox"];
	}

	public setSources(sources:InboxSource[]){
		this.sources = [...sources];
	}

	// toggleSource switches one of the chips of the "all" tab. Switching off the
	// last active one would leave an empty list, so we show everything again.
	public toggleSource(source:InboxSource){
		let i = this.sources.indexOf(source);
		if (i == -1) {
			this.sources.push(source);
		} else if (this.sources.length > 1) {
			this.sources.splice(i, 1);
		} else {
			this.sources = [...INBOX_SOURCES];
		}
	}

	public isSourceActive(source:InboxSource):boolean{
		return this.sources.indexOf(source) != -1;
	}

	public hasSelectionOfSource(source:InboxSource):boolean{
		return this.selection.some(p=>purchaseSource(p) == source);
	}

	public hasMixedSelection():boolean{
		return this.hasSelectionOfSource("peppol") && this.hasSelectionOfSource("other");
	}

	public createPurchase():Promise<Purchase>{
		if (this.hasMixedSelection()) {
			throw new Error("Cannot create a purchase out of peppol and non peppol documents at once");
		}
		let delegate:InboxTypeState = this.hasSelectionOfSource("peppol") ? this.peppolState : this.inboxState;
		delegate.selection = this.selection;
		return delegate.createPurchase();
	}
}
